/**
 * App
 *
 * This is the top-level component for the App.
 * All of the behavior starts here.
 */

import * as React from 'react'
import {
  ISearchResult,
  IContent,
  IModal,
  IPage
} from '../types'
import * as Api from '../service/api'
import * as Device from '../service/device'
import * as Event from '../service/event'
import * as Keys from '../service/keys'
import * as Logger from '../service/logger'
import * as Page from '../service/page'
import * as Util from '../service/util'
import NameSaver from '../service/name-saver'
import Search from '../service/search'
import * as Suggester from '../service/suggester'
import * as Content from '../service/content'
import * as UrlManager from '../service/url-manager'
import * as Pager from './pager'
import Input from './input'
import NameList from './name-list'
import Header from './header'
import Modal from './modal'
import Footer from './footer'


const nameSaver = new NameSaver('nameqlSavedNames')


export default class App extends React.Component<{}, {
  base: string
  savedNames: string[]
  modal: IModal
  content?: IContent
}> {

  search?: Search
  input: HTMLInputElement | null

  constructor(props) {
    super(props)

    const base = UrlManager.getBase()

    this.state = {
      base,
      savedNames: nameSaver.names,
      modal: undefined
    }

    if (base.length > 0) {
      this.startSearch(base)
    }

    document.addEventListener('keyup', this.onKeyUp)
  }

  componentWillUnmount() {
    document.removeEventListener('keyup', this.onKeyUp)
  }

  onKeyUp = event => {
    switch (event.which) {
      case Keys.ENTER:
        this.loadNextPage()
        break
      case Keys.UP:
        this.onPrevious()
        break
      case Keys.DOWN:
        this.onNext()
        break
    }
  }

  onNext = () => {
    const { content } = this.state

    if (!content) {
      return
    }

    if (content.index >= content.pages.length - 1) {
      this.loadNextPage()
    } else {
      content.index += 1
      this.setState({ content })
    }

    this.focusOnDesktop()
  }

  onPrevious = () => {
    const { content } = this.state

    if (!content) {
      return
    }

    if (content.index > 0) {
      content.index -= 1
      this.setState({ content })
    }

    this.focusOnDesktop()
  }

  onInputChange = (value: string) => {

    // Clean up the input text.
    const base = value.toLowerCase().replace(/[^a-z]/, '')

    // Do nothing if there wasn't a change.
    if (base == this.state.base) {
      return
    }

    // Set the base immediately so that the input is responsive.
    this.setState({ base })

    // Update the url.
    UrlManager.setBase(base)

    if (base.length == 0) {
      this.setContent(undefined)
    } else {
      this.startSearch(base)
    }

  }

  onSaveName = (name: string) => {
    if (this.state.savedNames.indexOf(name) >= 0) {
      this.setState({
        savedNames: this.state.savedNames.filter(n => n != name)
      })
      nameSaver.remove(name)
    } else {
      this.setState({
        savedNames: this.state.savedNames.concat(name)
      })
      nameSaver.add(name)
    }

    Event.track('saved_name', { name })
  }

  onContainerKeyUp = (event) => {
    switch (event.which) {
      case 13: // enter
        this.loadNextPage()
        break
      case 38: // up
        this.onPrevious()
        break
      case 40: // down
        this.onNext()
        break
    }
  }

  startSearch = (base: string) => {
    this.search = Suggester.createSearch(base)
    this.loadNextPage()
  }

  loadNextPage = () => {
    this.search && this.search.fetch().then(this.handleSearchResult)
  }

  getCurrentPage(): IPage | undefined {
    const { content } = this.state

    if (!content) {
      return
    }

    return Content.currentPage(content)
  }

  checkCurrentPage = Util.debounce(() => {
    const page = this.getCurrentPage()

    if (!page) {
      return
    }

    Logger.debug('Checking page')

    if (page.checked) {
      Logger.debug('Page already checked')
      return
    }

    Api.check(page.names).then(taken => {
      page.taken = new Set(taken)
      page.checked = true

      Logger.debug(`Done checking page: ${taken}`)
      this.forceUpdate()
    })

  }, 1000)

  handleSearchResult = (result: ISearchResult) => {
    const { content } = this.state

    // Ignore this result if there is no current search
    // Or if this search result isn't for the current search.
    if (!this.search || this.search.id != result.searchId) {
      return
    }

    // If this is the first page for this search, or there is
    // no result, then replace the result.
    if (result.page == 1 || !content) {

      this.setContent(Content.make(result.base, result.names))

      // Otherwise append this page to the current result.
    } else {

      this.setContent(Content.addPage(content, result.names))

    }

  }

  hasPrevious(): boolean {
    const { content } = this.state
    return !!content && Content.hasPrevious(content)
  }

  hasNext(): boolean {
    return !!this.state.content
  }

  setContent(content?: IContent) {
    this.setState({ content }, this.checkCurrentPage)
  }

  openModal = (modal: IModal) => {
    this.setState({ modal })
  }

  closeModal = () => {
    this.setState({ modal: undefined })
    this.focusOnDesktop()
  }

  openNameModal = (name: string) => {
    const page = this.getCurrentPage()

    const state = page ? Page.getNameState(page, name) : 'available'

    this.openModal({ type: 'name', name, state})
  }

  openSavedModal = () => {
    this.openModal({ type: 'saved' })
  }

  openAboutModal = () => {
    this.openModal({ type: 'about' })
  }

  openSettingsModal = () => {
    this.openModal({ type: 'settings' })
  }

  focusOnDesktop = () => {
    if (Device.IS_DESKTOP) {
      this.input && this.input.focus()
    }
  }

  openPrivacyModal = () => {
    this.openModal({ type: 'privacy' })
  }

  openFTCModal = () => {
    this.openModal({ type: 'ftc' })
  }

  renderContent() {
    const {
      content,
      base
    } = this.state

    if (!content) {
      if (Device.IS_DESKTOP && base.length == 0) {
        return (
          <div className="title">
            <h1>Type to find a great name<br /><small>with available .com</small></h1>
          </div>
        )
      } else {
        return null
      }
    }

    return (
      <NameList
        base={content.base}
        page={Content.currentPage(content)}
        onShowName={this.openNameModal}
      />
    )
  }

  render() {
    const {
      base,
      content,
      savedNames,
      modal
    } = this.state

    return (
      <div className="container">

        <Header
          savedNames={savedNames}
          onClickAbout={this.openAboutModal}
          onClickSaved={this.openSavedModal}
          onClickSettings={this.openSettingsModal}
        />

        <Input
          onRef={ref => this.input = ref}
          value={base}
          onChange={this.onInputChange}
        />

        {this.renderContent()}

        <Pager.Left
          isVisible={this.hasPrevious()}
          count={content && Content.numPagesBack(content)}
          onClick={this.onPrevious}
        />

        <Pager.Right
          isVisible={this.hasNext()}
          count={content && Content.numPagesForward(content)}
          onClick={this.onNext}
        />

        <Footer 
          onClickPrivacy={this.openPrivacyModal}
          onClickFTC={this.openFTCModal}
        />

        <Modal
          modal={modal}
          savedNames={savedNames}
          onClose={this.closeModal}
          onSaveName={this.onSaveName}
        />

      </div>
    )
  }

}


// WEBPACK FOOTER //
// ./src/comp/app.tsx